"use client";

import Link from "next/link";
import { useState, useTransition } from "react";
import { Star } from "lucide-react";
import {
  reportForumThreadAction,
  toggleForumSave,
} from "@/lib/forum-actions";

export function ForumThreadActions({
  threadId,
  currentUserId,
  isSaved,
}: {
  threadId: string;
  currentUserId: string | null;
  isSaved: boolean;
}) {
  const [optimisticSaved, setOptimisticSaved] = useState(isSaved);
  const [pendingAction, setPendingAction] = useState<"save" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  function handleSave() {
    if (!currentUserId || pendingAction) return;

    const previousSaved = optimisticSaved;
    setOptimisticSaved(!previousSaved);
    setPendingAction("save");
    setMessage(null);

    startTransition(async () => {
      try {
        const result = await toggleForumSave(threadId);
        if (!result.ok) {
          setOptimisticSaved(previousSaved);
          setMessage(result.message);
          return;
        }
        if (!result.data) throw new Error("missing_save_result");
        setOptimisticSaved(result.data.saved);
      } catch (error) {
        setOptimisticSaved(previousSaved);
        console.error("[forum-ui] thread save failed", error);
        setMessage("A mentés most nem sikerült.");
      } finally {
        setPendingAction(null);
      }
    });
  }

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={!currentUserId || pendingAction === "save"}
        onClick={handleSave}
        className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20 disabled:opacity-40 ${
          optimisticSaved ? "bg-[#273E47] text-white" : "border border-[#D8C99B]/70 bg-[#FCFBF8] text-[#273E47] hover:border-[#273E47]"
        }`}
      >
        {pendingAction === "save" ? "Saving..." : optimisticSaved ? "Saved" : "Save"}
      </button>
      <form action={async (formData) => {
        await reportForumThreadAction(formData);
      }}>
        <input type="hidden" name="threadId" value={threadId} />
        <input type="hidden" name="reason" value="Thread report" />
        <button
          disabled={!currentUserId}
          className="rounded-full border border-[#D8C99B]/70 bg-[#FCFBF8] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#273E47] transition hover:border-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:opacity-40"
        >
          Report
        </button>
      </form>
      {!currentUserId ? (
        <Link
          href="/login"
          className="rounded-full bg-[#A4243B] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
        >
          Login to interact
        </Link>
      ) : null}
      {message ? <p className="basis-full text-xs font-semibold text-[#A4243B]">{message}</p> : null}
    </div>
  );
}

export function ForumThreadCardQuickActions({
  threadId,
  href,
  currentUserId,
  initialSaved,
}: {
  threadId: string;
  href: string;
  currentUserId: string | null;
  initialSaved: boolean;
}) {
  const [saved, setSaved] = useState(initialSaved);
  const [pendingAction, setPendingAction] = useState<"save" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  function handleSave() {
    if (!currentUserId || pendingAction) return;
    const previousSaved = saved;
    setSaved(!previousSaved);
    setPendingAction("save");
    setMessage(null);
    startTransition(async () => {
      try {
        const result = await toggleForumSave(threadId);
        if (!result.ok) {
          setSaved(previousSaved);
          setMessage(result.message);
          return;
        }
        if (!result.data) throw new Error("missing_save_result");
        setSaved(result.data.saved);
      } catch (error) {
        setSaved(previousSaved);
        console.error("[forum-ui] thread quick save failed", error);
        setMessage("A mentés most nem sikerült.");
      } finally {
        setPendingAction(null);
      }
    });
  }

  return (
    <>
      <button
        type="button"
        disabled={!currentUserId || pendingAction === "save"}
        aria-label={saved ? "Unsave thread" : "Save thread"}
        onClick={handleSave}
        className={`rounded-full px-3 py-2 text-[10px] font-black uppercase transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/25 disabled:opacity-40 ${
          saved ? "bg-[#273E47] text-white" : "bg-[#EEF0EA] text-[#273E47] hover:bg-[#E7DFCC]"
        }`}
      >
        <span className="inline-flex items-center gap-1.5"><Star size={13} /> {pendingAction === "save" ? "..." : saved ? "Saved" : "Save"}</span>
      </button>
      <Link href={href} className="rounded-full bg-[#EEF0EA] px-3 py-2 text-[10px] font-black uppercase text-[#273E47] transition hover:bg-[#E7DFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8973C]/25">
        Open thread
      </Link>
      {message ? <span className="basis-full text-[10px] font-semibold text-[#A4243B]">{message}</span> : null}
    </>
  );
}
